import { makeStyles } from "@mui/styles";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import PrimaryButton from "@/components/UI/PrimaryButton/PrimaryButton";
import { ELEMENT_FORM_STATE, MIC_PERMISSION } from "@/constants";
import { setSimpleValue } from "@/store/actions";

import AudioPreview from "../AudioPreview/AudioPreview";

const useStyles = makeStyles({
  messageContainer: {
    width: "100%",
    height: "88px",
    background: "#161616",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  message: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    textAlign: "center",
    color: "#FFFFFF",
  },
  warning: {
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "24px",
    textAlign: "center",
    color: "#FF0000",
    "& p": {
      margin: "4px",
    },
  },
});

export default function VoiceMemoRecordBox(props) {
  const {
    element,
    formValues,
    setValue,
    selectedFile,
    setSelectedFile,
    elementFormState,
    setElementFormState,
    microphones,
    setMicrophones,
    previewUrl,
    setPreviewUrl,
    disabled,
  } = props;
  const { microphone, micPermission } = formValues;

  const dispatch = useDispatch();

  const classes = useStyles({ disabled });

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  }, [selectedFile?.name, element.id]);

  const requestPermission = async () => {
    try {
      setValue("micPermission", MIC_PERMISSION.DEFAULT);
      // request access/permission
      const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      // stop the tracks because we only use getUserMedia for permission popup
      stream.getTracks().forEach((track) => track.stop());

      const groups = {}; // avoid duplicate microphones
      let microphones = await navigator.mediaDevices.enumerateDevices();
      microphones = microphones.filter((x) => {
        if (x.kind !== "audioinput") {
          return false;
        }
        const isGroupAdded = groups[x.groupId];
        groups[x.groupId] = x;
        return !isGroupAdded;
      });

      let microphone = null;

      if (microphones[0]) {
        microphone = { id: microphones[0].deviceId, name: microphones[0].label };
      }

      setValue("micPermission", MIC_PERMISSION.GRANTED);
      setMicrophones(microphones.map((item) => ({ id: item.deviceId, name: item.label })));
      setValue("microphone", microphone);
    } catch {
      setValue("micPermission", MIC_PERMISSION.DENIED);
    }
  };

  useEffect(() => {
    requestPermission();
  }, []);

  const startRecording = () => {
    dispatch(
      setSimpleValue("voice-memo-recording", {
        elementId: element.id,
        open: true,
        deviceId: microphone?.id || "",
      }),
    );
  };

  return (
    <div data-testid="voice-memo-record-box" className={classes.recordBox}>
      {micPermission === MIC_PERMISSION.DEFAULT && (
        <div className={classes.messageContainer}>
          <div className={classes.message}>
            Please click <b>‘Allow’</b> to give access to your microphone.
          </div>
        </div>
      )}
      {(micPermission === MIC_PERMISSION.DENIED ||
        (micPermission === MIC_PERMISSION.GRANTED && !microphones.length)) && (
        <div className={classes.messageContainer}>
          <div className={classes.warning}>
            <p>Failed to detect your microphone.</p>
            <p>Please make sure your microphone is properly connected to begin recording.</p>
          </div>
        </div>
      )}
      {micPermission === MIC_PERMISSION.GRANTED && !selectedFile && !formValues.url && (
        <PrimaryButton onClick={startRecording} data-analytics={`${element.type}-record-start`}>
          Start Recording
        </PrimaryButton>
      )}
      <AudioPreview
        elementFormState={elementFormState}
        previewUrl={previewUrl}
        replace={() => {
          setSelectedFile(null);
          setValue("url", null);
          setElementFormState(ELEMENT_FORM_STATE.REPLACE);
        }}
        type={element.type}
      />
    </div>
  );
}
